/**
 * LLM verification functions — extracted from ArchitectureEnvironment.tsx
 * so they can be shared between the admin panel and the API key wizard.
 */

import { generateText as anthropicGenerateText } from '@/lib/anthropic';
import { generateText as geminiGenerateText } from '@/lib/gemini';
import { openaiCompatFetch } from './openaiProxyFetch';

export type VerifyStatus = 'idle' | 'verifying' | 'success' | 'error';

export interface VerifyResult {
  status: 'success' | 'error';
  errorMessage?: string;
}

// ── Gemini ──────────────────────────────────────────────────────────

export async function verifyGemini(apiKey: string, model: string): Promise<VerifyResult> {
  try {
    const { text } = await geminiGenerateText({
      apiKey,
      model,
      prompt: "Respond with the word 'READY' and nothing else.",
    });
    if (text.includes('READY')) {
      return { status: 'success' };
    }
    throw new Error('Unexpected response from Gemini Architect.');
  } catch (err: any) {
    return { status: 'error', errorMessage: err.message || 'Gemini authentication failed.' };
  }
}

// ── Claude ──────────────────────────────────────────────────────────

export async function verifyClaude(apiKey: string, model: string, proxyUrl: string): Promise<VerifyResult> {
  try {
    const { text } = await anthropicGenerateText({
      apiKey,
      baseURL: proxyUrl,
      model,
      userPrompt: "Respond with the word 'READY' and nothing else.",
      temperature: 1,
      maxTokens: 10,
    });
    if (text.includes('READY')) {
      return { status: 'success' };
    }
    throw new Error('Unexpected response from Claude Architect.');
  } catch (err: any) {
    let errorMessage = 'Claude authentication failed.';
    if (err.message?.includes('Failed to fetch') || err.name === 'TypeError') {
      errorMessage = `Connection error: Cannot reach proxy at ${proxyUrl}. Is the Cloudflare Worker running?`;
    } else if (err.message?.includes('fetch')) {
      errorMessage = `Network error: Cannot reach proxy at ${proxyUrl}. Check proxy URL and connection.`;
    } else if (err.status === 401) {
      errorMessage = 'Invalid API key. Check your Anthropic API key.';
    } else if (err.status === 404) {
      errorMessage = 'Invalid model name or endpoint. Verify the model ID.';
    } else if (err.status === 429) {
      errorMessage = 'Rate limit exceeded. Wait a moment and try again.';
    } else if (err.message?.includes('CORS')) {
      errorMessage = 'CORS error. Verify proxy is configured correctly.';
    } else if (err.error?.type === 'invalid_request_error') {
      errorMessage = `API Error: ${err.error.message || err.message}`;
    } else if (err.message) {
      errorMessage = err.message;
    }
    return { status: 'error', errorMessage };
  }
}

// ── OpenAI ──────────────────────────────────────────────────────────

export async function verifyOpenAI(apiKey: string, model: string): Promise<VerifyResult> {
  try {
    const response = await openaiCompatFetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${apiKey}` },
      body: JSON.stringify({
        model,
        max_tokens: 10,
        messages: [{ role: 'user', content: "Respond with the word 'READY' and nothing else." }],
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      if (response.status === 401) throw new Error('Invalid API key. Check your OpenAI API key.');
      if (response.status === 429) throw new Error('Rate limit exceeded. Wait a moment and try again.');
      throw new Error(errorData?.error?.message || `HTTP ${response.status}`);
    }

    const data = await response.json();
    const text = data.choices?.[0]?.message?.content || '';
    if (text.includes('READY')) {
      return { status: 'success' };
    }
    throw new Error('Unexpected response from OpenAI Architect.');
  } catch (err: any) {
    let errorMessage = 'OpenAI authentication failed.';
    if (err.message?.includes('Failed to fetch') || err.name === 'TypeError') {
      errorMessage = 'Network error: Cannot reach OpenAI API. Check your connection.';
    } else if (err.message) {
      errorMessage = err.message;
    }
    return { status: 'error', errorMessage };
  }
}

// ── xAI ─────────────────────────────────────────────────────────────

export async function verifyXAI(apiKey: string, model: string): Promise<VerifyResult> {
  try {
    const response = await fetch('https://api.x.ai/v1/chat/completions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${apiKey}` },
      body: JSON.stringify({
        model,
        max_tokens: 10,
        messages: [{ role: 'user', content: "Respond with the word 'READY' and nothing else." }],
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      if (response.status === 401) throw new Error('Invalid API key. Check your xAI API key.');
      if (response.status === 429) throw new Error('Rate limit exceeded. Wait a moment and try again.');
      throw new Error(errorData?.error?.message || `HTTP ${response.status}`);
    }

    const data = await response.json();
    const text = data.choices?.[0]?.message?.content || '';
    if (text.includes('READY')) {
      return { status: 'success' };
    }
    throw new Error('Unexpected response from xAI Architect.');
  } catch (err: any) {
    let errorMessage = 'xAI authentication failed.';
    if (err.message?.includes('Failed to fetch') || err.name === 'TypeError') {
      errorMessage = 'Network error: Cannot reach xAI API. Check your connection.';
    } else if (err.message) {
      errorMessage = err.message;
    }
    return { status: 'error', errorMessage };
  }
}
